/**
 * What this device has picked from the Go box: query → destination, newest
 * first. The matcher ranks a destination picked for a query the current text
 * begins above every other match, so the box learns "m" means Meals for you.
 *
 * Per-device localStorage on purpose: losing it costs a ranking hint, nothing
 * more. Storage throws in some privacy modes, so every access is guarded.
 */
const STORAGE_KEY = 'conway-go-picks';

/** Oldest picks fall off past this many. */
const MAX_PICKS = 100;

/** One remembered pick. */
export interface GoPick {
  query: string;
  id: string;
}

/** Normalised the way the matcher normalises a query. */
const normalise = (query: string): string => query.trim().toLowerCase();

/** The remembered picks, newest first; empty when storage is unavailable or garbled. */
export function loadGoPicks(): GoPick[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is GoPick =>
        typeof p === 'object' &&
        p !== null &&
        typeof (p as GoPick).query === 'string' &&
        typeof (p as GoPick).id === 'string'
    );
  } catch {
    return [];
  }
}

/** Remember that `query` led to destination `id`; a repeat moves to the front. */
export function recordGoPick(query: string, id: string): void {
  const q = normalise(query);
  if (!q) return;
  const rest = loadGoPicks().filter(p => !(p.query === q && p.id === id));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([{ query: q, id }, ...rest].slice(0, MAX_PICKS)));
  } catch {
    // Storage unavailable — the box simply does not learn on this device.
  }
}

/**
 * Destination ids picked for a query that `query` begins, newest first, each
 * once. "fo" recalls a pick made after typing "food"; "mail" does not recall
 * one made after typing "m".
 */
export function learnedIds(query: string, picks: readonly GoPick[]): string[] {
  const q = normalise(query);
  if (!q) return [];
  const ids = picks.filter(p => p.query.startsWith(q)).map(p => p.id);
  return [...new Set(ids)];
}
